/**
 * Unified Admin Preloader Component
 * 
 * Single Source of Truth for all admin authentication loading states.
 * Replaces 4 inconsistent preloader implementations with unified design.
 * 
 * Design Compliance:
 * - Uses semantic tokens from packages/config/theme.ts
 * - Follows golden ratio spacing system (--space-md, --space-lg)
 * - Standard motion timing (240ms cubic-bezier)
 * - WCAG AA accessibility compliance
 * 
 * States:
 * 1. loading    - Initial admin console loading
 * 2. checking   - Admin access verification
 * 3. processing - Authentication in progress (with optional card)
 * 4. success    - Authentication successful
 * 
 * @see CANON.md - UI/UX standards
 * @see docs/design-system/README.md - Design tokens
 */

import { SFIcon } from '@/components/ui/sf-icon';
import { sfIcons } from '@/lib/sf-icons';
import { InlineLoadingIndicator } from '@/components/ui/inline-loading';

export type AdminPreloaderVariant = 'loading' | 'checking' | 'processing' | 'success';

export interface AdminPreloaderProps {
  /**
   * Visual state variant
   */
  variant: AdminPreloaderVariant;

  /**
   * Override default title text
   */
  title?: string;

  /**
   * Override default message text
   */
  message?: string;

  /**
   * Show detailed card (only for 'processing' variant)
   */
  showCard?: boolean;

  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * Configuration for each preloader state
 */
const PRELOADER_CONFIG = {
  loading: {
    icon: (className: string) => (
      <InlineLoadingIndicator
        size="lg"
        className={className}
        aria-label="Loading admin console"
      />
    ),
    iconClass: 'inline-flex',
    defaultTitle: 'Loading admin console',
    defaultMessage: 'Checking admin access...',
    messageColor: 'text-muted-foreground',
    ariaLabel: 'Loading admin console',
  },
  checking: {
    icon: (className: string) => (
      <InlineLoadingIndicator
        size="lg"
        className={className}
        aria-label="Checking admin access"
      />
    ),
    iconClass: 'inline-flex',
    defaultTitle: 'Checking admin access',
    defaultMessage: 'Please wait...',
    messageColor: 'text-muted-foreground',
    ariaLabel: 'Checking admin access',
  },
  processing: {
    icon: (className: string) => (
      <InlineLoadingIndicator
        size="lg"
        className={className}
        aria-label="Processing authentication"
      />
    ),
    iconClass: 'inline-flex',
    defaultTitle: 'Admin Authentication',
    defaultMessage: 'Signing you in...',
    messageColor: 'text-primary',
    ariaLabel: 'Processing authentication',
    card: {
      icon: (className: string) => <SFIcon icon={sfIcons.lock} className={className} />,
      iconClass: 'h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5',
      title: 'Processing your authentication...',
      subtitle: 'This may take a few seconds.',
    },
  },
  success: {
    icon: (className: string) => <SFIcon icon={sfIcons.success} className={className} />,
    iconClass: 'h-6 w-6 text-success',
    defaultTitle: 'Admin Authentication',
    defaultMessage: 'Admin session established. Redirecting...',
    messageColor: 'text-success',
    ariaLabel: 'Authentication successful',
  },
} as const;

/**
 * Unified Admin Preloader Component
 * 
 * @example
 * ```tsx
 * // Basic usage
 * <AdminPreloader variant="loading" />
 * 
 * // With custom message
 * <AdminPreloader variant="checking" message="Verifying credentials..." />
 * 
 * // Processing with detailed card
 * <AdminPreloader variant="processing" showCard={true} />
 * 
 * // Success state
 * <AdminPreloader variant="success" />
 * ```
 */
export function AdminPreloader({
  variant,
  title,
  message,
  showCard = false,
  className = '',
}: AdminPreloaderProps) {
  const config = PRELOADER_CONFIG[variant];
  const displayTitle = title || config.defaultTitle;
  const displayMessage = message || config.defaultMessage;

  return (
    <main
      role="main"
      className={`min-h-screen bg-background flex items-center justify-center ${className}`}
      data-testid="admin-preloader"
      data-variant={variant}
      aria-busy="true"
    >
      <div
        className="flex flex-col items-center gap-4 p-8 text-center max-w-md"
        role="status"
        aria-live="polite"
        aria-label={`${config.ariaLabel}: ${displayMessage}`}
      >
        {/* Icon with standardized size and animation */}
        <div className="mb-2" aria-hidden="true">
          {config.icon(config.iconClass)}
        </div>

        {/* Title - consistent typography */}
        <h1 className="text-lg font-semibold text-foreground">
          {displayTitle}
        </h1>

        {/* Message - semantic color tokens */}
        <p className={`text-sm ${config.messageColor}`}>
          {displayMessage}
        </p>

        {/* Optional detailed card (processing state only) */}
        {showCard && variant === 'processing' && 'card' in config && config.card && (
          <div
            className="mt-6 p-4 rounded-lg border border-border bg-surface shadow-sm max-w-sm w-full"
            role="complementary"
            aria-label="Authentication details"
          >
            <div className="flex items-start gap-3">
              {config.card.icon(config.card.iconClass)}
              <div className="text-left flex-1">
                <p className="text-sm font-medium text-foreground">
                  {config.card.title}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {config.card.subtitle}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}

/**
 * Legacy compatibility wrapper for LoadingState
 * @deprecated Use AdminPreloader directly
 */
export function AdminLoadingState({ message }: { message?: string }) {
  return <AdminPreloader variant="loading" message={message} />;
}

// Default export for backward compatibility
export default AdminPreloader;

